import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {ReferralStats} from 'src/types/referrer-portal';

import {useDispatch, useSelector} from 'react-redux';
import {duotoneIconStyle} from 'src/utils/app-shell';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {getDateRangeOfDaysDifference} from 'src/components/referral-v2/common';
import {getTimeAgoOptions} from 'src/utils/referral-v2';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {
  getReferralStatsDetails,
  getReferralSocialLinks,
  getRecentActivities,
} from 'src/action-creators/referrer-portal/dashboard';
import {getUserDetails} from 'src/action-creators/referrer-portal/authentication';

import {
  BodyMedium,
  SimpleInlineDropdown,
  CircularLoader,
  KPIBox,
  PageTitle,
  PageName,
  TitleMedium,
  Icon,
} from '@spaced-out/ui-design-system/lib/components/index';
import {
  ReferralRequest,
  UserDetails,
  ReferralActivity,
  OverAllStatusInfo,
} from 'src/components/referrer-portal/dashboard/dashboard-component';

import css from 'src/components/referrer-portal/dashboard/dashboard.css';


const ReferralDashboard = () => {
  const {t} = useLabelTranslation();
  const timeAgoOptions = getTimeAgoOptions(t);
  // by default 7 days time frame duration
  const [timeFrame, setTimeFrame] = React.useState<string>('7');
  const [profileUrl, setProfileUrl] = React.useState<any>(null);

  const dispatch = useDispatch();
  const query = getDateRangeOfDaysDifference(timeFrame);

  const dependenciesLoading = useAsyncDependency(() => {
    const getProfilePic = () =>
      dispatch(reduxApi.get('user/picture')).then((data) =>
        setProfileUrl(data),
      );

    return Promise.all([
      dispatch(getReferralSocialLinks()),
      // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
      dispatch(getUserDetails()),
      getProfilePic(),
    ]);
  }, []);

  const dashboardDataLoading = useAsyncDependency(() => {
    return Promise.all([
      dispatch(getReferralStatsDetails(query)),
      dispatch(getRecentActivities(query)),
    ]);
  }, [timeFrame]);

  const compareDuration = timeAgoOptions
    .find((option) => option.key === timeFrame)
    ?.label?.toLowerCase();

  const referralStats: ReferralStats | null | undefined = useSelector(
    (state) => state.referrerPortal.referral_stats,
  );

  return (
    <>
      <PageTitle>
        <PageName>
          <TitleMedium>{t('Dashboard', 'Dashboard')}</TitleMedium>
          <Icon type="duotone" name="house" style={duotoneIconStyle} />
        </PageName>
      </PageTitle>
      <div className={css.children}>
        <SimpleInlineDropdown
          options={timeAgoOptions}
          selectedKeys={[timeFrame]}
          anchorPosition="bottom-start"
          classNames={{buttonWrapper: css.dateFIlter}}
          onOptionSelect={(option) => setTimeFrame(option.key)}
        >
          {timeAgoOptions.find((option) => option.key === timeFrame)?.label}
        </SimpleInlineDropdown>
        {dashboardDataLoading || dependenciesLoading ? (
          <div className={css.loader}>
            <CircularLoader colorToken="colorFillPrimary" size="medium" />
          </div>
        ) : (
          <div className={css.dashboard}>
            <div className={css.dashboardContainer}>
              <div className={css.overallContainer}>
                <div className={css.defaultGap}>
                  <KPIBox
                    classNames={{wrapper: css.kpiCard}}
                    semantic="neutral"
                    topContent={t('No_of_Referrals', 'No. of Referrals')}
                    middleContent={
                      <OverAllStatusInfo
                        overAllImpactInfo={referralStats?.no_of_referrals}
                      />
                    }
                    bottomContent={
                      <BodyMedium color="tertiary">
                        {`${t('compared_to', 'compared to')} `}
                        {compareDuration}
                      </BodyMedium>
                    }
                  />
                  <KPIBox
                    classNames={{wrapper: css.kpiCard}}
                    semantic="neutral"
                    topContent={`${t('Hired', 'Hired')} (${t(
                      'Active',
                      'Active',
                    )})`}
                    middleContent={
                      <OverAllStatusInfo
                        overAllImpactInfo={referralStats?.referred_to_hired}
                      />
                    }
                    bottomContent={
                      <BodyMedium color="tertiary">
                        {`${t('compared_to', 'compared to')} `}
                        {compareDuration}
                      </BodyMedium>
                    }
                  />
                  <KPIBox
                    classNames={{wrapper: css.kpiCard}}
                    semantic="neutral"
                    topContent={`${t('Pending_Payout', 'Pending Payout')}`}
                    middleContent={
                      <OverAllStatusInfo
                        showCurrency={true}
                        overAllImpactInfo={referralStats?.pending_payouts}
                      />
                    }
                    bottomContent={
                      <BodyMedium color="tertiary">
                        {`${t('compared_to', 'compared to')} `}
                        {compareDuration}
                      </BodyMedium>
                    }
                  />
                  <KPIBox
                    classNames={{wrapper: css.kpiCard}}
                    semantic="neutral"
                    topContent={`${t('Total_Earnings', 'Total Earnings')}`}
                    middleContent={
                      <OverAllStatusInfo
                        showCurrency={true}
                        overAllImpactInfo={referralStats?.total_earnings}
                      />
                    }
                    bottomContent={
                      <BodyMedium color="tertiary">
                        {`${t('compared_to', 'compared to')} `}
                        {compareDuration}
                      </BodyMedium>
                    }
                  />
                </div>
              </div>
              <div className={css.dashboardTable}>
                <ReferralRequest appliedDateFilter={timeFrame} />
              </div>
            </div>
            <div className={css.userContainer}>
              <UserDetails profileUrl={profileUrl} />
              <ReferralActivity />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ReferralDashboard;
